import { ReactNode, useState, useRef, useEffect } from "react";
import { 
  FileText, 
  Search, 
  Menu,
  Home,
  Star,
  X,
  PanelLeft
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { cn } from "@/lib/utils";
import type { TeamKnowledgeBaseEntry } from "@/types/team";
import { AppSidebarContent } from "@/components/AppSidebarContent";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";

interface ConfluenceLayoutProps {
  children: ReactNode;
  sidebar: ReactNode;
  searchQuery: string;
  onSearchChange: (query: string) => void;
  userName?: string;
  userAvatar?: string;
  entries?: TeamKnowledgeBaseEntry[];
  onEntrySelect?: (entryId: string) => void;
}

export const ConfluenceLayout = ({
  children,
  sidebar,
  searchQuery,
  onSearchChange,
  userName,
  userAvatar,
  entries = [],
  onEntrySelect
}: ConfluenceLayoutProps) => {
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [isMobileSearchOpen, setIsMobileSearchOpen] = useState(false);
  const [isAppSidebarOpen, setIsAppSidebarOpen] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const mobileInputRef = useRef<HTMLInputElement>(null);

  const initials = userName
    ? userName.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
    : 'U';

  // Filter entries based on search query
  const searchResults = searchQuery.length >= 2
    ? entries.filter(e => 
        e.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        e.content.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (e.category && e.category.toLowerCase().includes(searchQuery.toLowerCase()))
      ).slice(0, 6)
    : [];

  // Close search dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setIsSearchFocused(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleResultClick = (entryId: string) => {
    if (onEntrySelect) {
      onEntrySelect(entryId);
    }
    setIsSearchFocused(false);
    setIsMobileSearchOpen(false);
    setIsMobileSidebarOpen(false);
    onSearchChange('');
  };

  const getExcerpt = (content: string, query: string) => {
    const plainText = content.replace(/<[^>]*>/g, '');
    const lowerText = plainText.toLowerCase();
    const lowerQuery = query.toLowerCase();
    const index = lowerText.indexOf(lowerQuery);
    
    if (index === -1) {
      return plainText.slice(0, 100) + (plainText.length > 100 ? '...' : '');
    }

    const start = Math.max(0, index - 40);
    const end = Math.min(plainText.length, index + query.length + 60);
    let excerpt = plainText.slice(start, end);
    
    if (start > 0) excerpt = '...' + excerpt;
    if (end < plainText.length) excerpt = excerpt + '...';
    
    return excerpt;
  };

  const highlightMatch = (text: string, query: string) => {
    if (!query) return text;
    const regex = new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
    const parts = text.split(regex);
    
    return parts.map((part, i) => 
      regex.test(part) ? (
        <mark key={i} className="bg-yellow-200 dark:bg-yellow-500/30 px-0.5 rounded">
          {part}
        </mark>
      ) : part
    );
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#FAFBFC] dark:bg-background w-full">
      {/* App Sidebar as Sheet Overlay */}
      <Sheet open={isAppSidebarOpen} onOpenChange={setIsAppSidebarOpen}>
        <SheetContent 
          side="left" 
          className="!p-0 overflow-y-auto bg-sidebar text-sidebar-foreground border-sidebar-border" 
          style={{ width: '16rem', maxWidth: '16rem' }}
          hideCloseButton
        >
          <VisuallyHidden>
            <span>Navigation</span>
          </VisuallyHidden>
          <AppSidebarContent onNavigate={() => setIsAppSidebarOpen(false)} hideActiveState />
        </SheetContent>
      </Sheet>
      
      {/* Confluence-style Header */}
      <header className="h-14 bg-[#0052CC] dark:bg-primary flex items-center px-3 md:px-4 gap-2 md:gap-4 shrink-0 sticky top-0 z-50">
        {/* Left Section */}
        <div className="flex items-center gap-2 md:gap-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setIsAppSidebarOpen(true)}
            className="text-white/90 hover:text-white hover:bg-white/10 h-9 w-9"
          >
            <Menu className="h-5 w-5" />
          </Button>
          
          {/* Mobile Sidebar Toggle */}
          <Sheet open={isMobileSidebarOpen} onOpenChange={setIsMobileSidebarOpen}>
            <SheetTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="text-white/90 hover:text-white hover:bg-white/10 h-9 w-9 md:hidden"
              >
                <PanelLeft className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="p-0 w-72">
              {sidebar}
            </SheetContent>
          </Sheet>
          
          <div className="flex items-center gap-2">
            <span className="font-semibold text-white text-base md:text-lg hidden sm:block">Wissensbank</span>
          </div>
        </div>

        {/* Center - Search with Dropdown (Desktop) */}
        <div className="flex-1 max-w-xl mx-auto hidden md:block" ref={searchRef}>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/60" />
            <Input
              ref={inputRef}
              placeholder="In Wissensbank suchen..."
              value={searchQuery}
              onChange={(e) => onSearchChange(e.target.value)}
              onFocus={() => setIsSearchFocused(true)}
              className="pl-10 pr-10 h-9 bg-white/10 border-white/20 text-white placeholder:text-white/60 focus:bg-white/20 focus:border-white/40 focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0"
            />
            {searchQuery && (
              <Button
                variant="ghost"
                size="icon"
                className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 text-white/60 hover:text-white hover:bg-transparent"
                onClick={() => {
                  onSearchChange('');
                  inputRef.current?.focus();
                }}
              >
                <X className="h-4 w-4" />
              </Button>
            )}

            {/* Search Results Dropdown */}
            {isSearchFocused && searchQuery.length >= 2 && (
              <div className="absolute top-full left-0 right-0 mt-2 bg-white dark:bg-card rounded-lg shadow-xl border overflow-hidden z-50">
                {searchResults.length > 0 ? (
                  <ScrollArea className="max-h-[400px]">
                    <div className="p-2">
                      <p className="text-xs text-muted-foreground px-2 py-1 mb-1">
                        {searchResults.length} Ergebnis{searchResults.length !== 1 ? 'se' : ''} gefunden
                      </p>
                      {searchResults.map((entry) => (
                        <button
                          key={entry.id}
                          onClick={() => handleResultClick(entry.id)}
                          className="w-full text-left p-3 rounded-md hover:bg-[#F4F5F7] dark:hover:bg-muted transition-colors group"
                        >
                          <div className="flex items-start gap-3">
                            <div className="h-8 w-8 rounded bg-[#DEEBFF] dark:bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                              <FileText className="h-4 w-4 text-[#0052CC] dark:text-primary" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <h4 className="font-medium text-foreground group-hover:text-[#0052CC] dark:group-hover:text-primary truncate">
                                  {highlightMatch(entry.title, searchQuery)}
                                </h4>
                                {entry.is_pinned && (
                                  <Star className="h-3 w-3 text-amber-500 fill-amber-500 flex-shrink-0" />
                                )}
                              </div>
                              <p className="text-sm text-muted-foreground line-clamp-2 mt-0.5">
                                {highlightMatch(getExcerpt(entry.content, searchQuery), searchQuery)}
                              </p>
                              {entry.category && (
                                <span className="inline-block text-xs text-[#0052CC] dark:text-primary bg-[#DEEBFF] dark:bg-primary/10 px-1.5 py-0.5 rounded mt-1">
                                  {entry.category}
                                </span>
                              )}
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  </ScrollArea>
                ) : (
                  <div className="p-6 text-center">
                    <Search className="h-8 w-8 text-muted-foreground/50 mx-auto mb-2" />
                    <p className="text-sm text-muted-foreground">
                      Keine Ergebnisse für "{searchQuery}"
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Mobile Search Toggle */}
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setIsMobileSearchOpen(!isMobileSearchOpen)}
          className="text-white/90 hover:text-white hover:bg-white/10 h-9 w-9 md:hidden ml-auto"
        >
          <Search className="h-5 w-5" />
        </Button>

        {/* Right Section */}
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="icon"
            className="text-white/80 hover:text-white hover:bg-white/10 hidden sm:flex"
          >
            <Home className="h-5 w-5" />
          </Button>
          
          <Avatar className="h-7 w-7 md:h-8 md:w-8 ml-1 md:ml-2 border-2 border-white/30">
            <AvatarImage src={userAvatar} />
            <AvatarFallback className="bg-white/20 text-white text-xs md:text-sm">
              {initials}
            </AvatarFallback>
          </Avatar>
        </div>
      </header>

      {/* Mobile Search Bar */}
      {isMobileSearchOpen && (
        <div className="bg-[#0052CC] dark:bg-primary px-3 pb-3 md:hidden">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/60" />
            <Input
              ref={mobileInputRef}
              placeholder="Suchen..."
              value={searchQuery}
              onChange={(e) => onSearchChange(e.target.value)}
              onFocus={() => setIsSearchFocused(true)}
              className="pl-10 pr-10 h-10 bg-white/10 border-white/20 text-white placeholder:text-white/60 focus:bg-white/20 focus:border-white/40 focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0"
              autoFocus
            />
            {searchQuery && (
              <Button
                variant="ghost"
                size="icon"
                className="absolute right-1 top-1/2 -translate-y-1/2 h-8 w-8 text-white/60 hover:text-white hover:bg-transparent"
                onClick={() => {
                  onSearchChange('');
                  mobileInputRef.current?.focus();
                }}
              >
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>

          {/* Mobile Search Results */}
          {searchQuery.length >= 2 && (
            <div className="mt-2 bg-white dark:bg-card rounded-lg shadow-lg border overflow-hidden">
              {searchResults.length > 0 ? (
                <ScrollArea className="max-h-[50vh]">
                  <div className="p-2">
                    <p className="text-xs text-muted-foreground px-2 py-1 mb-1">
                      {searchResults.length} Ergebnis{searchResults.length !== 1 ? 'se' : ''}
                    </p>
                    {searchResults.map((entry) => (
                      <button
                        key={entry.id}
                        onClick={() => handleResultClick(entry.id)}
                        className="w-full text-left p-3 rounded-md hover:bg-muted transition-colors"
                      >
                        <div className="flex items-center gap-2">
                          <FileText className="h-4 w-4 text-[#0052CC] dark:text-primary flex-shrink-0" />
                          <span className="font-medium text-foreground truncate">
                            {highlightMatch(entry.title, searchQuery)}
                          </span>
                          {entry.is_pinned && (
                            <Star className="h-3 w-3 text-amber-500 fill-amber-500 flex-shrink-0" />
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                </ScrollArea>
              ) : (
                <div className="p-4 text-center">
                  <p className="text-sm text-muted-foreground">
                    Keine Ergebnisse
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Main Content Area */}
      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar (Desktop) */}
        <aside className="w-64 bg-white dark:bg-card border-r border-border shrink-0 hidden md:flex flex-col">
          {sidebar}
        </aside>

        {/* Main Content */}
        <main className="flex-1 overflow-auto">
          {children}
        </main>
      </div>
    </div>
  );
};

export default ConfluenceLayout;
